import React, { useMemo, useState } from 'react'

import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import type { Theme } from '@mui/material'
import {
  Area,
  Bar,
  ComposedChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'

import dayjs from 'dayjs'

import type {
  IRoiSpendPoint,
  IRoiValuePoint,
  RoiValueGranularity,
  VeraSpendSource,
} from '@repo/types'

import { ChartColumnIcon, ChartLineIcon } from 'lucide-react'

// Direct file import, not the `components` barrel — this module is itself re-exported from
// `components/analytics/index.ts`, and routing through the top-level barrel to reach a
// sibling is how an import cycle gets built by accident.
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'

import { formatMoneyAxis, formatMoneyCentsCompact } from './analyticsMoney'

/**
 * ── THE TWO INPUTS OVER TIME: VALUE DELIVERED AND SPEND WITH VERA ───────────────────
 *
 * Both series are real history. Value delivered sums genuinely DATED events — resale value
 * keyed on `transfers.created_at`, stolen-recovery value on the `return` action log's
 * `created_at`. Spend is settled Stripe invoices: money that was actually paid, at the
 * price actually charged at the time, bucketed on its payment date. Nothing on this chart
 * is projected or prorated.
 *
 * ✅ SPEND USED TO BE REFUSED HERE, and the refusal is worth remembering because the fix
 * was to find a real source rather than to relax the rule. `company_subscriptions` holds
 * ONE row per company with no plan history, so the only spend point derivable from it was
 * today's price projected backwards — a figure that renders as precisely as a real one and
 * is partly invented. A summary can carry that with one caveat line; a line chart cannot,
 * because a reader takes the SHAPE of a line, not its footnotes. Invoice-backed spend
 * (`vera-spend-invoice.source.ts`) removed the objection entirely.
 *
 * ✅ THE RUNNING ROI MULTIPLE IS NOW DRAWABLE, AS AN EXCLUSIVE THIRD FRAME. It was
 * rejected as a co-plotted series and that rejection stands — see the exclusivity block in
 * `CHART_SERIES` for what was rendered, why it lost, and why "turn it on and the currency
 * pair goes off" is a different proposition rather than the same one with a switch. It is
 * still on the TOOLTIP in every frame, which is where a ratio can be exact without claiming
 * a shape.
 *
 * ⚠️ IT USED TO BE OFF BY DEFAULT, ALWAYS. That is no longer the rule, and re-reading the
 * old argument is worth doing before "fixing" this back — it is not wrong, it is
 * SUPERSEDED, and by a mechanism the old argument didn't have available. Return on Vera is
 * the card's whole subject (the 56px headline is its endpoint), so product direction
 * (2026-08-28) is that it should LEAD — win the brand-purple colour and the first legend
 * slot, and be the frame a reader meets on open — the moment its shape can be trusted.
 * "Can be trusted" is doing real work: see `ROI_GATE_MIN_SPEND_BUCKETS` for what disqualifies
 * a company from that default and `ROI_GATE_REASON` for what they see instead. Two things
 * are both true and don't contradict: the running multiple is never drawn from an unstable
 * head (below), and a company past that gate sees it FIRST, unprompted, every time.
 *
 * ── THE MARK: BARS ARE THE HONEST FORM, LINE IS THE DEFAULT BY PRODUCT CHOICE ───────
 *
 * ⚠️ Read the argument BEFORE changing the default back. It is still correct as an
 * argument; it was overridden knowingly, and deleting it is how it gets re-derived from
 * scratch by someone who assumes the line was never questioned.
 *
 * ## The argument for bars, which has not been retracted
 *
 * `BillingOverview`'s activations chart switches to a smoothed area for DAILY buckets, on
 * the grounds that daily activations are a genuine trend. Value delivered is not the same
 * shape of quantity: it moves in discrete events — one resale, one recovery — so most
 * buckets are exactly zero and the rest are lumps. An area through `0, 0, 0, 4200, 0` reads
 * as a gentle swell centred on a day nothing happened, which is precisely the failure that
 * chart's own comment describes; the sparsity is just more extreme here, so the exception
 * applies at every granularity rather than only at monthly. Bars say "nothing, nothing,
 * nothing, then $42", which is what occurred.
 *
 * ## Why the default is a LINE anyway
 *
 * Explicit product direction (2026-08-28): this chart is to behave like "Scans by time",
 * which offers a Line / Bar toggle and opens on a line. Two charts on one page whose mark
 * is chosen by different rules — one fixed, one switchable — is a real cost of its own, and
 * it is the cost the reader pays rather than the author.
 *
 * The override is narrower than it looks, because the toggle is what makes it safe: the
 * honest mark is one click away and permanently offered, so a reader who suspects the line
 * is smoothing over an empty week can check in a second. That is a materially different
 * situation from shipping a line and no bars at all, which is what the argument above was
 * written against.
 *
 * Two mitigations carry the rest of it, and both are load-bearing rather than decorative:
 *   - ❌ DOTS WERE ON in line mode, and are now OFF (`dot={false}`). The argument for them is
 *     kept because it is a real argument and will otherwise be re-derived: `ScansByTime2`
 *     plots 30–720 dense contiguous buckets where a dot per point is noise, whereas this one
 *     plots at most ~30 sparse ones, so a dot marked each bucket that was actually MEASURED
 *     and made the segments between them visibly interpolation rather than data.
 *
 *     ⚠️ IT WAS OVERRULED ON EXPLICIT PRODUCT DIRECTION (2026-08-28): *"ROI chart lines have
 *     dots on the line, the other doesn't, get rid of the dots."* PARITY WITH THE CHART
 *     DIRECTLY BENEATH IT WINS. The reasoning is the same one that put the Line/Bar toggle
 *     here in the first place: two charts stacked on one page whose marks are drawn by
 *     different rules is a cost the READER pays, and it is paid on every glance, whereas the
 *     interpolation subtlety the dots guarded against is paid once by a reader who is already
 *     looking closely. That reader still has the bar toggle, which states the sparsity
 *     exactly rather than hinting at it, and `activeDot` still marks whatever they hover.
 *   - THE AREA FILL IS GATED TO A SINGLE VISIBLE SERIES, which is a narrower rule than the
 *     "no fill, ever" this line used to state. That refusal was on the grounds that a fill
 *     shades the region under an INTERPOLATED segment and so amplifies the "gentle swell
 *     over a day nothing happened" failure. Explicit product direction (2026-08-28) asks
 *     for `ScansByTime2`'s treatment including the gradient, and the objection is answered
 *     rather than waived: the fill only appears in the one state where the reader has
 *     ISOLATED a series, i.e. has stopped comparing and is reading one shape. (An earlier
 *     version of this line also leant on the resting dots staying visible THROUGH the fill;
 *     that half no longer holds now the dots are off, and it is struck rather than quietly
 *     left standing. The isolation argument is the one that was doing the work.)
 *     Bars still never take one. See `filledSeriesId` for the compositing argument that
 *     makes "exactly one" the rule rather than a preference.
 *
 * ## Unlike `ScansByTime2`, there is no AUTO rule
 *
 * That chart keeps a `defaultMarkFor` seam running underneath a user override. As of
 * VRG-512 it resolves to a constant `'line'` — the bucket-count rule it used to run was
 * retired when month bucketing made a year's data 12 buckets, i.e. SPARSE by that rule's
 * own threshold, which would have defaulted every long range to bars. The reasoning here
 * is unchanged and was always the stronger form of it: value delivered is sparse at EVERY
 * granularity, so a bucket-count rule would have had nothing to decide on this chart
 * either. A rule that cannot change its mind is a constant with extra steps.
 *
 * ── WHY ONE SERIES AND NOT TWO STACKED ─────────────────────────────────────────────
 *
 * The response carries both contributors per bucket so a reader can see which one moved,
 * and the obvious rendering is a two-colour stack. It is not used: a stack needs a legend
 * and a second categorical colour, which is real chrome inside a disclosure panel on a card
 * whose subject is a single 56px figure. The breakdown is on the tooltip instead, where
 * someone asking "which one?" will look, and it is exact there rather than estimated from a
 * segment height.
 */

/** Matches the panel proportions elsewhere on this page without being a panel. */
const CHART_HEIGHT = 220

// `fmtCurrency` / `fmtAxisCurrency` used to hardcode 'USD' (and the axis literally
// interpolated `$`) — moved to `analyticsMoney.ts` (VERA-260), which threads the
// company's `reporting_currency` through instead. See that module's docblock.

/**
 * The tick label for a bucket START.
 *
 * A WEEK bucket reads as its start date rather than as "week 34": an ISO week number is
 * unreadable to anyone not already thinking in them, and the start date is the same
 * information in a form the reader can locate on a calendar.
 */
const bucketLabel = (iso: string, granularity: RoiValueGranularity): string => {
  const d = dayjs(iso)
  if (!d.isValid()) return ''
  return granularity === 'month' ? d.format('MMM YYYY') : d.format('MMM D')
}


/**
 * A plotted row: the bucket's own fields, its axis label, and one optional key per series
 * (`value` / `spend` / `roi`) carrying that series' number when the payload has it.
 *
 * The series keys are OPTIONAL, which is the type-level statement of the same rule the
 * chart enforces at runtime: a series the API does not send is absent from the row rather
 * than present-and-zero.
 */
type ChartDatum = RoiChartPoint & { label: string } & Partial<
    Record<ValueChartSeriesId, number>
  >

type ValueChartMark = 'line' | 'bar'

/**
 * LINE first and selected on open — see the mark block above for why the default is a line
 * at all, and why the bar option is not a nicety.
 *
 * Icons, via `ViewToggle`, NOT `SegmentedTabs`: chart type is a per-card view preference
 * and the segmented pill is the control for switching what is COUNTED. That component's
 * own docblock carries the full argument; the short version is that the pill's volume said
 * this decision was as consequential as the page's Scans / Certificates switch, and it is
 * not. `ScansByTime2` uses the identical pair, so the two charts still read as one system.
 *
 * `ChartLine` / `ChartColumn` are the conventional glyphs — a reader decodes them without a
 * legend, which is the property that makes iconifying THIS control legitimate where it
 * would not be for the map's "All scans" / "Latest location" (no established glyph exists
 * for either, so that one stays words at the same weight).
 */
const MARK_OPTIONS: readonly ViewToggleOption<ValueChartMark>[] = [
  { value: 'line', label: 'Line', icon: ChartLineIcon },
  { value: 'bar', label: 'Bar', icon: ChartColumnIcon },
]

/**
 * ── TWO SERIES, BOTH CURRENCY, ONE AXIS ─────────────────────────────────────────────
 *
 * The CONFIG declares what the chart can draw; the RENDER draws whatever the payload
 * actually has. `activeSeries` tests the DATA, never a feature flag or a hardcoded list:
 *
 *   const activeSeries = CHART_SERIES.filter(s => merged.some(p => typeof s.read(p) === 'number'))
 *
 * So a range with no invoice in it draws value alone and suppresses the legend, and a range
 * with one draws both and shows it — no branch anywhere else. There is NO stub, NO
 * placeholder and NO flat zero line for an absent series: a drawn zero is a CLAIM that
 * spend was zero, which is a different statement from "we have no spend point here".
 *
 * ── THE ROI MULTIPLE IS A THIRD SERIES, AND IT IS MUTUALLY EXCLUSIVE WITH THE PAIR ──
 *
 * 🚨 IT MUST NEVER SHARE A FRAME WITH VALUE AND SPEND. Read this whole block before
 * "fixing" the exclusivity into an additive toggle — a dual axis was built here once,
 * rendered against real buckets, looked at, and rejected. Every objection that killed it is
 * an objection to COHABITATION, not to the series:
 *
 *  - It DOMINATED the chart. The running multiple is the only series continuous across
 *    every bucket (value arrives in sparse lumps, spend in monthly ones), so it became the
 *    tallest, longest, most saturated shape on a card whose subject is the other two.
 *    Alone in its own frame it is not competing with anything, so there is nothing to
 *    dominate.
 *  - The two lines CROSSED with no cue which scale either belonged to. At one bucket the
 *    value line read ~$260 on the left while the ROI line read ~11.7× on the right, and a
 *    reader taking either against the wrong axis gets a confident wrong number. With one
 *    axis on screen at a time there is exactly one scale to read against, so the ambiguity
 *    cannot arise.
 *  - `dataviz` states it as a non-negotiable: *"One axis. Never a dual-axis chart (two
 *    y-scales)."* Exclusivity SATISFIES that rule rather than working around it. At any
 *    instant this chart has one y-axis and every drawn mark belongs to it.
 *
 * ⚠️ ONE OBJECTION SURVIVED EXCLUSIVITY AND IS NOW HANDLED SEPARATELY: the shape is partly
 * an ARTEFACT. `running_multiple` is CUMULATIVE, so it steps down mechanically on every
 * bucket where spend accrues and value does not, and its left end is wild — a first bucket
 * with one invoice and one recovery can read 12× while the settled figure is 2.9×. The early
 * VALUES are not wrong (cumulative value ÷ cumulative spend genuinely is ~12× after one
 * invoice); what is wrong is the SHAPE a descending line puts on a denominator that is
 * simply still growing — it reads as "return is deteriorating" when nothing deteriorated.
 *
 * That used to be the reason the whole series stayed off by default. It no longer is: the
 * fix is to not DRAW the misleading part rather than to hide the honest rest of it — see
 * `ROI_GATE_MIN_SPEND_BUCKETS`, which trims exactly the unstable head and nothing past it.
 * Once trimmed, the series leads by default (see the file-level note above). The tooltip's
 * exact "Return to date N×" row is UNAFFECTED by the trim — it still reports whatever
 * `running_multiple` the API actually sent for the hovered bucket, wild or not, because a
 * tooltip states one number at one point and never claims a shape across the ones beside it.
 * The legend row keeps naming it "Return on Vera (to date)" for the same reason it always
 * did — see that entry in `CHART_SERIES`.
 *
 * ── WHY EXCLUSIVE RATHER THAN ADDITIVE ──
 *
 * The temptation, once the legend can hide series, is to let all three be on together and
 * trust the reader to hide what they do not want. That is the dual axis again with an extra
 * step: the DEFAULT state of an additive toggle set is "everything visible", so the
 * rejected rendering becomes the one every reader meets first. Exclusivity makes the
 * mixed frame unreachable rather than merely discouraged.
 *
 * Mechanically: `frame` is `'currency' | 'ratio'`, one y-axis is visible per frame, and
 * every mark is mounted with `hide` (never conditionally unmounted) so recharts' "an axis
 * requires a corresponding yAxisId on the targeted graphical component" invariant — which
 * throws through the global ErrorBoundary and presents as a HOME crash — can never fire.
 *
 * ⚠️ `apps/api`'s `roi-value-series.guard.spec.ts` asserts a VALUE POINT carries no spend
 * or multiple field, and that is still correct and still passing. It is not contradicted by
 * anything here: the API sends spend as a SEPARATE bucket-aligned array (`spend_series`),
 * and the join happens in `mergedPoints`, on the client, for recharts' benefit only.
 */
type ValueChartSeriesId = 'value' | 'spend' | 'roi'

/**
 * The two FRAMES. Exactly one is on screen at a time — see the exclusivity block above.
 * Named as its own type because `frame` state, the `<YAxis>` ids and `ValueChartSeries.axis`
 * must be the same closed set; three independent string literals is how one of them
 * eventually names an axis nothing declares, which is a crash rather than a typo.
 */
type ValueChartAxis = 'currency' | 'ratio'

/**
 * ONE ROW OF THE PLOT: a value point with its spend counterpart folded in.
 *
 * ⚠️ THE API SENDS TWO ARRAYS, NOT ONE OBJECT, and that is deliberate on its side — see
 * `IRoiValuePoint`'s own note. A value point comes from Postgres event rows and a spend
 * point from settled Stripe invoices; the two carry different completeness guarantees, and
 * one merged object would let a caller read a spend figure without ever meeting
 * `IVeraSpend.source` / `exact`, which is the only thing that says whether the denominator
 * is trustworthy. The API's guard spec pins that (`roi-value-series.guard.spec.ts` — "a
 * value point stays value only"), and it is still passing.
 *
 * Merging is a RENDERING concern, and it happens here because recharts addresses a series
 * by a flat `dataKey` on one row. The fields keep the API's names (`spend_cents`,
 * `running_multiple`) rather than being renamed on the way in, so a reader can grep either
 * side and land on the other.
 *
 * OPTIONAL, and that is the type-level statement of the rule the chart enforces at runtime:
 * a series the response did not carry is ABSENT from the row rather than present-and-zero.
 */
type RoiChartPoint = IRoiValuePoint & {
  /** Cash the brand actually paid in this bucket. `IRoiSpendPoint.spend_cents`. */
  spend_cents?: number
  /**
   * Cumulative value ÷ cumulative spend AT this bucket — a RUNNING figure, not a
   * bucket-local ratio. `IRoiSpendPoint.running_multiple`.
   *
   * `null`, never `Infinity`, for every bucket before the range's first invoice. That is
   * the normal opening state of a monthly plan, not an edge case, and it is computed
   * server-side precisely so no client divides by zero.
   */
  running_multiple?: number | null
}

interface ValueChartSeries {
  id: ValueChartSeriesId
  /** Legend and tooltip label. Matches the hero figure it is the history of, word for word. */
  label: string
  /**
   * Which y-axis it belongs to — and, since the frames are exclusive, which FRAME it is a
   * member of. Only one axis is ever visible at a time (see the exclusivity block above).
   *
   * `yAxisId` has to be stated explicitly on both the axis and every mark: recharts throws
   * an invariant (taking the page down through the global ErrorBoundary) if an axis mounts
   * with no graphical component carrying its id, or vice versa. Naming the axis in the
   * config is what guarantees the two can never be written independently.
   */
  axis: ValueChartAxis
  /**
   * `toggle` follows the Line/Bar control; `line` is always a line.
   *
   * Only the value series is toggleable, and that is a claim about the DATA rather than a
   * limitation. Value arrives as discrete, unpredictable events, so bars are its honest
   * form and the toggle offers it. Spend is a RECURRING CHARGE on a known cadence: its
   * lumps are the billing cycle rather than anything the brand did, and drawn as bars
   * beside value's they would compete for the same categorical slot and read as two
   * comparable event streams. A thin line says "this is the standing cost the other series
   * is measured against", which is what it is.
   */
  mark: 'toggle' | 'line'
  color: (theme: Theme) => string
  /**
   * For the tooltip row. The AXIS formatter is per-axis, not per-series.
   *
   * `currency` is optional and only meaningful for the two money series (`value` /
   * `spend`) — `roi`'s implementation ignores it, formatting a ratio instead.
   */
  format: (value: number, currency?: string) => string
  read: (point: RoiChartPoint) => number | null | undefined
}

/**
 * ── LISTED IN LEGEND / DEFAULT-DRAW ORDER, NOT IN INTRODUCTION ORDER ────────────────
 *
 * `roi` is declared FIRST even though value and spend were the original pair, because
 * array order is what `activeSeries` (a `CHART_SERIES.filter(...)`) and, through it, the
 * legend row order are both derived from — see `legendRows`. Product direction (2026-08-28)
 * puts Return on Vera first: it is the card's whole subject, it now leads the default frame
 * (see the file-level note above `CHART_SERIES`'s declaration), and the legend should read
 * top-to-bottom in the same priority a reader is meant to notice things in.
 */
const CHART_SERIES: readonly ValueChartSeries[] = [
  {
    id: 'roi',
    /**
     * "(to date)" is load-bearing, not decoration. `running_multiple` is CUMULATIVE within
     * the requested range — value-so-far ÷ spend-so-far — so a legend row reading plainly
     * "Return on Vera" would name the same thing as the 56px headline above while plotting
     * something the headline is not: the headline is the figure at the END of the range,
     * and this is the path to it. The tooltip's own row uses the same words for the same
     * reason.
     */
    label: 'Return on Vera (to date)',
    axis: 'ratio',
    /**
     * NEVER bars, and it is not a matter of taste. A bar encodes a magnitude from a zero
     * baseline and invites comparison BETWEEN categories; a cumulative ratio is neither —
     * bucket 7's 3.1× is not "3.1 units of something that happened in bucket 7", it is the
     * whole range so far restated. Bars would say it was, twelve times over.
     */
    mark: 'line',
    /**
     * `brand[500]` — THE card's colour, not a slot in a categorical rotation. Return on
     * Vera is what the 56px headline above this chart states, so the series that traces it
     * takes the same purple everything else on this card that means "the answer" already
     * uses. This is a reassignment, not a new pick: `brand[500]` used to belong to `value`
     * (see that entry below for why it moved and why cyan is safe for value in its place).
     *
     * NOT `primary.main` — the primary ramp is an INK token (literally #000000 light /
     * #ffffff dark), so a series painted with it would carry the same value as body text
     * and read as chrome rather than data. Same reasoning the activations chart uses.
     */
    color: theme => theme.palette.brand[500],
    format: value => `${value.toFixed(1)}×`,
    read: point => point.running_multiple,
  },
  {
    id: 'value',
    label: 'Value delivered',
    axis: 'currency',
    mark: 'toggle',
    /**
     * ✅ `green[600]` `#16A249` — product direction, 2026-09-02, chosen from four
     * candidates shown side by side against `brand[500]`.
     *
     * REPLACES `cyan[600]` `#0896AF`, which was rejected as too turquoise. The cyan
     * validation note is preserved under `spend` below and is still correct on its own
     * terms; it simply stopped being the pick.
     *
     * The pair that matters is value ↔ ROI, not value ↔ spend: ROI shares no frame with
     * spend, but a reader toggling frames sees value then ROI in SEQUENCE and must never
     * confuse them from memory. Green sits further from `brand[500]`'s purple in hue than
     * cyan did, so that separation improves rather than regresses.
     *
     * 🚫 Green is safe here ONLY because nothing on this card is red. A red/green pair is
     * the single worst combination under deuteranopia (~8% of men), and red is already
     * semantically taken on this PAGE — the map's invalid/tampered scan pills
     * (`rgba(220,38,38,…)`). If a red series is ever introduced to THIS card, this pick
     * has to be revisited; the constraint is the pairing, not the hue on its own.
     *
     * Money-positive by convention, which suits the numerator of the figure this card is
     * proud of — and it is not the "success" green of a status chip, which lives on a
     * different ramp stop and never appears on this chart.
     */
    color: theme => theme.palette.green[600],
    // Currency is a fact about the figure (which rows are in the aggregate), not a
    // display preference — see `analyticsMoney.ts`.
    format: (cents, currency) => formatMoneyCentsCompact(cents, currency),
    read: point => point.value_cents,
  },
  {
    id: 'spend',
    label: 'Spend with Vera',
    axis: 'currency',
    mark: 'line',
    /**
     * ⚠️ MUTED, DELIBERATELY, OVERRULING THE EARLIER VALIDATED PICK. This series used to be
     * `cyan[600]` — validated below, and that validation is still correct as far as it
     * goes — but explicit product direction (2026-08-30) rejects a saturated hue for spend
     * on a different ground than the accuracy the validator checks: spend is not a thing
     * that happened TO the brand, it is the price the brand chose to pay and the
     * DENOMINATOR of the number this card is proud of. A fully saturated colour equal in
     * weight to value and ROI editorialises it as a third comparable event stream; muting
     * it says "this is the standing cost the other two are measured against" without a
     * word of copy.
     *
     * 🚫 NOT RED, and not merely "an option that wasn't picked" — it was proposed for this
     * exact slot and is wrong twice over. (a) Red is already semantically TAKEN on this
     * page: it is the invalid/tampered-scan colour on the map's status pills
     * (`rgba(220,38,38,…)`), so reusing it here means red means "counterfeit" on one card
     * and "your subscription cost" on another. (b) It frames Vera's own pricing as harm,
     * which spend is not.
     *
     * 🚫 NOT A RED/GREEN PAIR with anything else on this card, for the standard reason: it
     * is the single worst combination under deuteranopia (~8% of men), where the two hues
     * collapse toward each other. Moot here since nothing on this card is red, but the
     * constraint is why `green[600]` staying on `roi` was reconsidered and kept — see that
     * entry's own note.
     *
     * `theme.palette.gray[400]` — chosen FROM THE NEUTRAL RAMP as directed, not a bespoke
     * hex, and not `gray[500]` (the token this chart tried once before, at `spend`, and
     * rejected — see the validation note that follows). `gray[500]` fails for a reason that
     * matters more now than it did then: in DARK mode it is remapped (via `darkNeutral`) to
     * `#a1a1aa`, which is the theme's `text.secondary` — the EXACT colour the chart's own
     * axis ticks are painted (`tick: { fill: theme.palette.text.secondary }`). Measured,
     * that is a contrast ratio of 1.00:1 against the axis text — not close to it, IDENTICAL
     * to it — which is precisely the "reads as chart furniture" failure the validation
     * below already diagnosed once for a different token. `gray[400]` does not collide:
     *   gray[400] light `#81858D`  L 52.9%  ·  vs axis text (~L 40%, MUI default 0.6α
     *                                            black) → 1.55:1, clearly a distinct tone
     *   gray[400] dark  `#71717a`  L 46.1%  ·  vs `text.secondary` `#a1a1aa` (L 65%) →
     *                                            1.89:1, clearly darker/heavier than the
     *                                            axis labels, not equal to them
     * `gray[400]` and `gray[500]` share the ramp's dark-mode remap machinery (`gray`/`grey`
     * are both flipped via `darkNeutral`, unlike `brand`/`cyan`, which stay fixed hex in
     * both modes), so this stays mode-aware without a manual light/dark branch here.
     *
     * ✅ LIGHTNESS-SEPARABLE FROM THE OTHER TWO, computed directly rather than assumed —
     * chroma alone does not prove "won't be confused with a chromatic line", lightness
     * does, and it is what a colour-blind reader falls back on once hue stops helping:
     *   value    cyan[600]   `#0896AF`           HSL L 35.9%  ·  S 91.3% (chromatic)
     *   roi      brand[500]  `#8F68EA`           HSL L 66.3%  ·  S 75.6% (chromatic)
     *   spend    gray[400]   light 52.9 / dark 46.1%  ·  S 3.8–5.0% (ACHROMATIC)
     * Spend sits BETWEEN value and roi in lightness in both modes (light: 35.9 < 52.9 <
     * 66.3; dark: 35.9 < 46.1 < 66.3) — never the same lightness as either neighbour, ΔL
     * ≥ 10.2 pts against the tighter of the two (cyan, dark mode). Being ACHROMATIC is what
     * makes that margin sufficient rather than marginal: deuteranopia and protanopia distort
     * HUE discrimination, not lightness, and a colour with ~4–5% saturation has no hue for
     * either simulation to distort in the first place — it cannot drift toward cyan or
     * purple under any CVD type the way two chromatic hues can drift toward each other. So
     * the three-way set is safe by construction: value/roi are the validated chromatic pair
     * (below), and spend is differentiated from both by lightness alone, which every CVD
     * type preserves.
     *
     * ── THE ORIGINAL VALIDATION (still true; the token it validated moved to `value`) ──
     * ✅ VALIDATED — `cyan[600]` `#0896AF`, run against BOTH card surfaces because the
     * theme's neutral ramps are REPLACED WHOLESALE in dark mode, so a single-mode check
     * proves nothing about the other:
     *
     *   gray[500]  light #ffffff  → FAIL chroma floor 0.012 ("reads gray")
     *   cyan[600]  light #ffffff  → PASS all four · worst all-pairs ΔE 35.5 (deutan),
     *                               tritan 37.1, normal 78.1
     *   cyan[600]  dark  #0f0f12  → PASS all four · same ΔE figures, contrast ≥ 3:1
     *
     * Rejected alternatives — `blue[500]` FAILS CVD against brand purple (protan ΔE 5.5,
     * the classic blue/purple collision); `cyan[500]` passes light but leaves the dark
     * lightness band (0.715 > 0.67); `green[600]` passes everything but is a reserved
     * STATUS hue and would read as "good" on a chart line meaning "cost" — an objection
     * that does not apply to `roi`, where green would have been the connotation pointing
     * the right way; `roi` uses `brand[500]` regardless, for the reasons in that entry.
     */
    color: theme => theme.palette.gray[400],
    // Same rationale as `value` above — one reporting currency, threaded through.
    format: (cents, currency) => formatMoneyCentsCompact(cents, currency),
    read: point => point.spend_cents,
  },
]

/**
 * What Value delivered is allowed to count, in one sentence.
 *
 * It began as the second sentence of the empty state, which made a fact about SCOPE
 * readable only in the one case where there was nothing to apply it to. A reader looking
 * at a $4,200 bar has the same question ("four thousand of what?"), so the empty state
 * keeps one short sentence about THIS PERIOD and the scope moved out to the hero.
 *
 * ⚠️ IT NOW OPENS THE `Partial · N of M sources` TOOLTIP, not the chart disclosure's.
 * Sitting beside the disclosure it explained the chart; sitting on the sources line it
 * explains the caveat, which is the question a reader actually has ("why 2 of 5?"). See
 * `RoiHero`'s `sourcesExplanation`, which composes this with what is MISSING — the half
 * this sentence has never carried.
 *
 * ⚠️ COUNT-AGNOSTIC, deliberately. It read "the two dated sources Vera can price today",
 * which hard-codes the number of contributors into a string that sits next to a live
 * `N of M` derived from the response. The moment a third became computable the two would
 * contradict each other on the same line, and the wrong one would be the confident one.
 *
 * Exported so the tooltip and the chart read one string (§44's argument applied to copy
 * that is not a glossary term: two call sites, one source).
 */
export const ROI_VALUE_CHART_SCOPE =
  'Value delivered counts only what Vera can price from dated events: completed resales, and stolen or lost items that came back. Nothing here is projected.'

/**
 * ── WHY THE RETURN LINE CANNOT BE DRAWN, BY CAUSE ──────────────────────────────────
 *
 * 🚨 THIS IS WHAT A READER WHO ASKED FOR THE ROI LINE ACTUALLY MEETS, so it has to name
 * the real cause rather than the most common one. The line is not "off"; it has no points.
 * `running_multiple` is `null` at every bucket whenever cumulative spend is zero (the API
 * computes it that way on purpose, see `runningMultiple` — `null`, never `∞`), so `roi`
 * never enters `activeSeries` and there is no series to plot, no axis to mount and, before
 * this, no legend row either. A reader was left looking for a control that did not exist.
 *
 * ⚠️ A DISABLED ROW THAT NAMES THE CAUSE, NOT AN ENABLED ONE THAT DRAWS NOTHING. Making it
 * clickable-but-empty is strictly worse: it answers "where is the line?" with a blank frame
 * and an axis, which reads as a rendering bug rather than as an absent denominator. §46's
 * forcing function applies directly — a disabled control MUST carry a sentence saying why,
 * and if no true sentence can be written the control should be hidden instead. Every member
 * below has one, so the row is shown.
 *
 * ⚠️ KEYED ON `IVeraSpend['source']`, and EXHAUSTIVE BY TYPE. A `Record<VeraSpendSource, …>`
 * rather than a `switch` with a default: a new member of the union then fails the BUILD
 * instead of silently inheriting whichever sentence the default happened to hold. That
 * matters more here than it looks, because the members disagree about something a reader
 * acts on — `no_billing_account` has nothing to do with the selected window, and
 * `unavailable` means the spend is UNKNOWN rather than zero. A shared fallback sentence
 * would state "no spend in this period" over all four, which is wrong for two of them and
 * dangerously wrong for the last.
 */
const ROI_UNAVAILABLE_REASON: Record<VeraSpendSource, string> = {
  /**
   * ⚠️ MOSTLY SUPERSEDED, KEPT FOR TYPE EXHAUSTIVENESS AND THE ONE CASE IT STILL COVERS.
   * When `spendSource === 'stripe_invoices'` and the ratio is unavailable, `roiUnavailableReason`
   * (below `ROI_GATE_REASON`) checks the billing-period GATE first and this string is only
   * reached when the gate itself has nothing to say — i.e. zero settled buckets in the window,
   * where "at least two billing periods" would be true but unhelpfully vague next to a plain
   * "there is nothing here yet". A brand mid-history who narrows the date filter to a week
   * with no invoice in it still meets this, unchanged, and "the period IS the reason" is still
   * correct for them.
   */
  stripe_invoices: 'No Vera spend settled in this period, so there is nothing to divide by.',
  /**
   * ⚠️ NOT ABOUT THE PERIOD. A brand in trial, comped, or never provisioned has no Stripe
   * customer at all, so widening the range cannot help and a sentence blaming the window
   * sends the reader to the period select to no effect.
   */
  no_billing_account:
    'This company has no Vera billing account yet, so there is no spend to divide by.',
  /**
   * ⚠️ UNKNOWN, NOT ZERO. Stripe was unreachable, so the denominator was not measured. The
   * API deliberately reports zero rather than falling back to a projection, and this is the
   * sentence that stops that zero being read as a fact about the brand's spending.
   */
  unavailable:
    'Vera could not load billing history just now, so return cannot be calculated.',
  /**
   * Not served to clients today (`getRoiMetrics` always replaces a projection), but the
   * union permits it, so it gets a true sentence rather than an assumption that it cannot
   * arrive. A projected denominator is not something to draw a return curve from.
   */
  plan_projection:
    'Vera spend for this period is estimated rather than billed, so no return is drawn from it.',
}

/**
 * ── THE HEAD TRIM: WHY EARLY BUCKETS ARE WITHHELD FROM `running_multiple`, NOT THE WHOLE
 *    SERIES ──────────────────────────────────────────────────────────────────────────
 *
 * Read this alongside the "ONE OBJECTION SURVIVED EXCLUSIVITY" note above `CHART_SERIES` —
 * that note names the defect (a cumulative ratio's left end is a real number wearing a
 * misleading SHAPE), this constant is the fix.
 *
 * `2`, not `1` and not a dollar amount. One settled invoice cannot produce a stable
 * running multiple — the very next invoice necessarily moves it, often a lot, which is
 * the mechanism behind the "12× first bucket, 2.9× settled" example. A SECOND settled
 * bucket is the earliest point at which the ratio has already absorbed one such move and
 * is no longer purely a function of a single payment.
 *
 * DERIVED FROM THE DATA ACTUALLY RETURNED (a count of buckets carrying non-zero settled
 * spend), never a hardcoded currency floor. Plans differ in price — a fixed dollar amount
 * would gate a $4,900/mo enterprise plan on its first invoice alone while leaving a
 * $99/mo plan waiting on a much smaller absolute figure for the same two invoices. A
 * bucket COUNT behaves identically regardless of what the brand pays per period.
 *
 * See `mergedPoints` for where the trim is applied (it nulls `running_multiple` on every
 * point before the count reaches this threshold, leaving `spend_cents` and `value_cents`
 * on those points untouched — only the ratio's unstable head is withheld) and
 * `spendBucketsSettled` for the count itself.
 */
const ROI_GATE_MIN_SPEND_BUCKETS = 2

/**
 * ── WHY RETURN NO LONGER LEADS: THE ONE MESSAGE, SHOWN TWICE ────────────────────────
 *
 * Return on Vera leads this chart's default frame once it clears the gate above (see the
 * file-level note before `CHART_SERIES`). Before that, a brand meeting the default
 * currency frame with no explanation would have no way to tell "not yet" from "never" —
 * exactly the failure §46 exists to prevent for a disabled control, except here the
 * control that's missing is an entire FRAME rather than one row.
 *
 * So this string is used in TWO places, deliberately the same string rather than two that
 * could drift (the file's own single-source instinct, applied to copy rather than to a
 * glossary term): the disabled `roi` legend row's tooltip (hover-discoverable, matching
 * every other disabled-row reason on this card), AND a VISIBLE inline banner rendered in
 * the chart body itself (`roiUnavailableReason`, `roiComingSoon` in the component). The
 * banner exists because hover-only would be a silent fallback for the one case this whole
 * mechanism was built to avoid — a brand in month one has no reason to go hunting a
 * disabled legend row for an explanation of a frame they were never shown.
 */
const ROI_GATE_REASON =
  'Return needs at least two billing periods. Value delivered and spend are shown until then.'

/**
 * ── A LINE NEEDS TWO POINTS: THE GATE `ratioAvailable` WAS MISSING ──────────────────
 *
 * 🚨 THIS IS THE STATE THE "WHY THE RETURN LINE CANNOT BE DRAWN" NOTE ABOVE FORBIDS,
 * reached from the other side. That note reasons about a series with NO points and
 * concludes the legend row must be disabled. `ratioAvailable` then implemented "has
 * anything to plot at all" as `activeSeries.some(axis === 'ratio')` — AT LEAST ONE numeric
 * bucket — and one bucket is not a line. recharts emits `M<x>,<y>Z` for it: a moveto and a
 * closepath, zero length, nothing on screen. So the legend row rendered ENABLED, the ratio
 * frame led by default, the axis mounted and labelled itself, and the plot was empty —
 * exactly the "blank frame and an axis, which reads as a rendering bug" that note calls
 * STRICTLY WORSE than a disabled row.
 *
 * Observed live on the intro-seed tenant: the ROI `<Area>` rendered `d="M1938,44.673Z"`
 * while the Scans chart beside it drew a full curve. Two settled spend buckets cleared
 * `ROI_GATE_MIN_SPEND_BUCKETS`, the head trim then nulled the first — by design — and the
 * lone survivor became a point pretending to be a series.
 *
 * `2`, and it is NOT the same `2` as `ROI_GATE_MIN_SPEND_BUCKETS` despite both being two.
 * That constant asks "is the ratio STABLE enough to show?" and is about billing history;
 * this asks "can a polyline be drawn from what survived the trim?" and is about geometry.
 * They are independent — the trim removes a point AFTER the gate is met — so a company can
 * pass the first and fail the second, which is the state above. Collapsing them into one
 * number would re-couple two questions that answer to different things.
 *
 * ⚠️ NOT FIXED BY LOOSENING THE TRIM. Withholding the unstable head is correct (see
 * `ROI_GATE_MIN_SPEND_BUCKETS`); the defect was claiming a drawable series afterwards
 * without checking what survived it.
 */
const ROI_MIN_PLOTTABLE_POINTS = 2

/**
 * The one-point case, stated as what it is rather than as a fault.
 *
 * Distinct from `ROI_GATE_REASON`, which means "no return is defined yet". This means "a
 * return IS defined, for exactly one period, and one period is a dot" — a reader who has
 * just read a multiple in the hero would call the gate sentence simply wrong, because for
 * them it is. Naming the shape ("a point rather than a line") also answers the question
 * that state actually provokes: why a number that plainly exists is not on the chart.
 */
const ROI_SINGLE_POINT_REASON =
  'Return is defined for only one period so far, which is a point rather than a line. The curve appears once a second period settles.'

/**
 * The hover body: the bucket, one row per DRAWN series, and the two contributors behind
 * Value delivered.
 *
 * ## Two levels, and they are not the same thing
 *
 * SERIES rows come from `series` — whichever of value / spend / ROI the payload actually
 * carries — so the tooltip grows with the chart rather than being rewritten when the
 * second and third series land. The first is emphasised because with one series it IS the
 * answer; with three it is still the one the card's headline divides.
 *
 * CONTRIBUTOR rows (Recovered / Resale) sit UNDER the value row and belong to it
 * specifically. They stay rendered ONLY when non-zero: a bucket that is entirely one
 * source would otherwise carry a `$0` row that says nothing, and the panel's job is to
 * answer "which one moved?" — a zero is not an answer, it is the absence of one.
 *
 * Typed loosely on purpose: recharts' own `TooltipProps` generics have shifted between
 * versions in ways that produce noise rather than safety, and everything read here is
 * narrowed at the point of use.
 */
const ValueTooltip = ({
  active,
  payload,
  label,
  series = [],
  currency,
}: {
  active?: boolean
  payload?: Array<{ payload?: ChartDatum }>
  label?: string | number
  series?: readonly ValueChartSeries[]
  /** The company's reporting currency — a fact about the figures, not a display choice. */
  currency?: string
}) => {
  const theme = useTheme()
  const datum = payload?.[0]?.payload
  if (!active || !datum) return null

  const contributors: Array<[string, number]> = [
    ['Recovered', datum.stolen_recovery_cents],
    ['Resale', datum.resale_value_cents],
  ]

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        boxShadow: theme.shadows[2],
        px: 3,
        py: 2,
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>

      {series.map(s => {
        const raw = s.read(datum)
        if (typeof raw !== 'number') return null
        const isValue = s.id === 'value'
        return (
          <Box key={s.id}>
            <Typography
              variant={isValue ? 'body3' : 'caption'}
              fontWeight={isValue ? 600 : 400}
              display="block"
              // The label is dropped while `value` is the ONLY series: the figure is
              // already titled by the card and by the summary line, and a lone
              // "Value delivered $350" row on a one-series chart is a restatement. It
              // appears the moment there is a second series to tell it apart from.
              sx={{ color: series.length > 1 ? s.color(theme) : undefined }}
            >
              {series.length > 1 ? `${s.label} ` : ''}
              {s.format(raw, currency)}
            </Typography>

            {isValue &&
              contributors
                .filter(([, cents]) => cents > 0)
                .map(([name, cents]) => (
                  <Typography key={name} variant="caption" color="text.secondary" display="block">
                    {name} {formatMoneyCentsCompact(cents, currency)}
                  </Typography>
                ))}
          </Box>
        )
      })}

      {/*
        ── THE RUNNING MULTIPLE LIVES HERE, NOT ON A LINE ────────────────────────────

        This is the whole of what replaced the rejected ROI series (see `CHART_SERIES`). A
        tooltip can state a ratio EXACTLY, at the one bucket the reader pointed at, without
        claiming a shape across every other bucket — and shape was the entire problem: a
        running multiple steps down mechanically wherever spend accrues without value, so
        drawn as a line it narrates a decline that did not happen.

        "to date" is load-bearing copy, not filler. `running_multiple` is CUMULATIVE within
        the requested range — value-so-far ÷ spend-so-far — not this bucket's own ratio, and
        a bare "Return 3.9×" on a row of per-bucket figures would be read as the latter.

        `typeof === 'number'`, so the row is absent rather than zero for every bucket before
        the range's first invoice, where the API sends `null` precisely because the honest
        answer is "not yet defined" rather than "0.0×" or `∞`.
      */}
      {typeof datum.running_multiple === 'number' && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Return to date {datum.running_multiple.toFixed(1)}×
        </Typography>
      )}
    </Box>
  )
}

export const RoiValueChart = ({
  points,
  spendPoints,
  spendSource,
  granularity = 'day',
  rangeLabel,
  currency,
}: {
  points: IRoiValuePoint[]
  /**
   * The DENOMINATOR over the same buckets, from `roi.spend_series`.
   *
   * Optional because the caller has to ask for it (`?spend_series=true`), and absent is a
   * real state rather than an error: a response without it draws value alone, exactly as
   * this chart did before spend existed. See `mergedPoints` for how a missing array and an
   * all-null series are told apart.
   */
  spendPoints?: IRoiSpendPoint[]
  /**
   * `IVeraSpend.source` — the PROVENANCE of the denominator, not a number from it.
   *
   * Passed in for exactly one purpose: when the return line has no points, this is the only
   * thing that can say WHY. The chart can see that every `running_multiple` is null; it
   * cannot see whether that is because no invoice settled in the window, because the brand
   * has no billing account at all, or because Stripe was unreachable — three causes a
   * reader would act on differently. See `ROI_UNAVAILABLE_REASON`.
   *
   * Optional so a caller that has not got it yet degrades to a cause-free sentence rather
   * than to a confidently wrong one.
   */
  spendSource?: VeraSpendSource
  /**
   * Chosen by the SERVER from the range length and reported back, never asked for by the
   * client — see the API's note. The default only covers a response predating the field.
   */
  granularity?: RoiValueGranularity
  /** The window these buckets cover, for the summary line. */
  rangeLabel?: string
  /**
   * `IRoiMetrics.reporting_currency` — a fact about the figures (which rows are in the
   * aggregate), not a display preference (VERA-260). Optional, with no local default: the
   * shared formatters already fall back to `DEFAULT_REPORTING_CURRENCY`.
   */
  currency?: string
}) => {
  const theme = useTheme()

  /**
   * Plain `useState`, unlike `ScansByTime2`'s module store.
   *
   * That chart needs an external store because its toggle renders into the card SHELL's
   * header while the chart renders into the shell's body — siblings with no common owner.
   * Here the toggle and the plot are in ONE component, so the ordinary answer is available
   * and the store would be ceremony. (It also means the mark resets when the disclosure is
   * collapsed and re-opened, which is correct: it is a view preference on a panel, not a
   * saved setting — the same status `ScansByTime2` gives its own brush window.)
   */
  const [mark, setMark] = useState<ValueChartMark>('line')

  /**
   * ── THE TWO RESPONSE ARRAYS, JOINED INTO ONE ROW PER BUCKET ────────────────────────
   *
   * 🚨 EVERYTHING DOWNSTREAM READS THIS, NOT `points`. That is the whole correctness
   * argument, not a style preference: `activeSeries` decides which marks are drawn AND
   * whether the ratio axis mounts, and recharts throws an invariant — taking the page down
   * through the global ErrorBoundary — if an axis mounts with no graphical component
   * carrying its `yAxisId`. Deriving the row data from the merged points while deriving
   * `activeSeries` from the raw ones is exactly how those two disagree. One source.
   *
   * JOINED BY BUCKET KEY, not by index. The API guarantees equal length and byte-identical
   * `bucket` strings at matching indices (`IRoiSummary.spend_series`), so a positional join
   * would be correct today — but it is correct by TRUST, and it fails silently and
   * plausibly if that ever slips: every spend figure lands one bucket out and the chart
   * still renders beautifully. A keyed join degrades to "no spend on that row" instead,
   * which is visible.
   */
  const mergedPoints = useMemo<RoiChartPoint[]>(() => {
    if (!spendPoints?.length) return points
    const byBucket = new Map(spendPoints.map(p => [p.bucket, p]))
    const joined: RoiChartPoint[] = points.map(point => {
      const spend = byBucket.get(point.bucket)
      if (!spend) return point
      return {
        ...point,
        spend_cents: spend.spend_cents,
        running_multiple: spend.running_multiple,
      }
    })

    /**
     * ── TRIM THE UNSTABLE HEAD OF `running_multiple` ──────────────────────────────────
     *
     * See `ROI_GATE_MIN_SPEND_BUCKETS` for the full argument. Walking buckets in order
     * (the API returns them chronologically) and counting DISTINCT buckets that actually
     * settled spend — not cumulative spend crossing some dollar line, a bucket COUNT — every
     * point before the count reaches the gate has its `running_multiple` nulled. Only that
     * one field is touched: `spend_cents` and `value_cents` on the same points are left
     * exactly as joined, so the currency frame's shape (and the tooltip's per-bucket rows)
     * are entirely unaffected by this trim. Once the gate is met the API's own cumulative
     * figure is trusted for every remaining point, wild or not — the trim removes exactly
     * the unstable head and nothing past it.
     */
    let spendBucketsSeen = 0
    return joined.map(point => {
      if (typeof point.spend_cents === 'number' && point.spend_cents > 0) {
        spendBucketsSeen += 1
      }
      if (spendBucketsSeen < ROI_GATE_MIN_SPEND_BUCKETS) {
        return { ...point, running_multiple: null }
      }
      return point
    })
  }, [points, spendPoints])

  /**
   * Distinct buckets that actually settled spend, across the whole merged window —
   * the SAME count `mergedPoints` uses to decide the trim, recomputed here (cheap; these
   * arrays are ~30 points) because the trim only needs to know when the gate was CROSSED,
   * while the unavailable-reason copy (`roiUnavailableReason` below) needs to know how many
   * buckets exist at all, to tell "zero settled" apart from "one settled, one short of the
   * gate" — two states with different, non-interchangeable sentences.
   */
  const spendBucketsSettled = useMemo(
    () =>
      mergedPoints.reduce(
        (n, p) => n + (typeof p.spend_cents === 'number' && p.spend_cents > 0 ? 1 : 0),
        0,
      ),
    [mergedPoints],
  )

  /**
   * Each bucket, plus its axis label, plus ONE KEY PER SERIES the payload actually carries.
   *
   * The projection exists because recharts addresses a series by a flat `dataKey` while the
   * config addresses it by a `read` accessor, and the accessor is what lets a series be
   * "absent" rather than zero. A key is written only when `read` returns a number, so a
   * `<Line dataKey="spend">` could not be given a silent `undefined → 0` even by mistake.
   *
   * ⚠️ This is also what keeps a `running_multiple: null` bucket — every bucket before the
   * range's first invoice — as a GAP in the ROI line rather than a plotted zero. recharts
   * omits a point whose `dataKey` is undefined; a zero there would assert that the return
   * was zero in a month where it was simply not yet defined.
   */
  const data = useMemo(
    () =>
      mergedPoints.map(point => {
        const row: ChartDatum = {
          ...point,
          label: bucketLabel(point.bucket, granularity),
        }
        CHART_SERIES.forEach(series => {
          const value = series.read(point)
          if (typeof value === 'number') row[series.id] = value
        })
        return row
      }),
    [mergedPoints, granularity],
  )

  const total = useMemo(() => points.reduce((sum, p) => sum + p.value_cents, 0), [points])

  /**
   * The tallest CURRENCY bucket across every currency series, which decides whether the
   * left axis has a range to label at all.
   *
   * ⚠️ NOT `value_cents` alone, which is what it read before spend existed. A period with
   * no recoveries and no resales but a paid invoice is entirely ordinary — and with a
   * value-only maximum it took the all-zero branch below, collapsing the axis to a single
   * `$0` tick while a spend line climbed past it. The axis would then be labelling one
   * series and describing another.
   */
  const maxValue = useMemo(
    () =>
      mergedPoints.reduce((max, point) => {
        const perSeries = CHART_SERIES.filter(s => s.axis === 'currency').map(s => {
          const raw = s.read(point)
          return typeof raw === 'number' ? raw : 0
        })
        return Math.max(max, ...perSeries)
      }, 0),
    [mergedPoints],
  )

  /**
   * ── WHICH SERIES ARE ACTUALLY DRAWN — decided by the DATA, never by a flag ──────────
   *
   * See the `CHART_SERIES` block for the full argument. The seam this line was built for
   * has since been used: `spend_series` now arrives, so this returns `[value, spend]` on a
   * range containing an invoice, `[value]` on one that does not, and the legend appears and
   * disappears with it — no edit here was needed.
   *
   * ⚠️ IT ALSO GOVERNS THE AXIS. Every mark declares `yAxisId={series.axis}` and the only
   * `<YAxis>` declares the same id, so the set of drawn marks and the set of mounted axes
   * are derived from this ONE expression. Deriving them separately is how recharts' "an
   * axis with no corresponding graphical component" invariant gets thrown — a crash that
   * escapes through the global ErrorBoundary and presents as a Home crash.
   *
   * `typeof === 'number'` and not a truthiness test: a bucket of exactly `0` is a real
   * measurement (a week in which nothing was recovered) and must not read as a missing
   * series, which is the same distinction the empty state above turns on.
   */
  const activeSeries = useMemo(
    () => CHART_SERIES.filter(s => mergedPoints.some(p => typeof s.read(p) === 'number')),
    [mergedPoints],
  )

  /**
   * How many buckets the ALREADY-TRIMMED ratio series can actually put on screen.
   *
   * Counted from `mergedPoints` (post-trim) rather than from the raw `spendPoints`, because
   * the trim is precisely what makes those two disagree: a payload can carry two non-null
   * `running_multiple` buckets and still leave one once the unstable head is withheld.
   */
  const ratioPlottablePoints = useMemo(
    () => mergedPoints.reduce((n, p) => n + (typeof p.running_multiple === 'number' ? 1 : 0), 0),
    [mergedPoints],
  )

  /**
   * Whether the (already-trimmed) ratio series can be DRAWN — not merely whether it exists.
   *
   * Both halves are load bearing. The `activeSeries` half keeps this in step with what
   * actually mounts (axes and marks derive from that one expression, and the recharts
   * invariant in the render block depends on them agreeing). The point-count half is the
   * fix argued at `ROI_MIN_PLOTTABLE_POINTS`: a single surviving bucket is a number, not a
   * curve, and treating it as a series produced an enabled legend row over an empty frame.
   *
   * ⚠️ `roi` DELIBERATELY STAYS IN `activeSeries` when this is false. Dropping it there
   * would unmount the ratio axis and the ROI mark together — safe in itself, but it would
   * make axis membership depend on a SECOND expression, which is the drift the render
   * block's invariant note warns about. Nothing stray is painted regardless: with this
   * false the frame stays on currency, so the mark is hidden by `hide={!isVisible(series)}`
   * and the ratio `<YAxis>` by its own `hide={frame !== 'ratio'}`, both on the ordinary path.
   *
   * Computed here, ahead of the `frameChoice` state below, specifically so that state's
   * lazy initializer can read it — see that hook for why the default frame depends on this
   * rather than always opening on currency.
   */
  const ratioAvailable =
    activeSeries.some(s => s.axis === 'ratio') && ratioPlottablePoints >= ROI_MIN_PLOTTABLE_POINTS

  /**
   * ── VISIBILITY: A FRAME, PLUS A HIDDEN SET WITHIN THE CURRENCY FRAME ────────────────
   *
   * Two pieces of state rather than one hidden set, because the two axes are exclusive and
   * a single set cannot express that without every reader of it re-deriving the rule.
   *
   *  `frame`          which y-axis is on screen. `'ratio'` draws ROI alone; `'currency'`
   *                   draws whichever of value / spend is not hidden.
   *  `hiddenCurrency` the reader's selection WITHIN the currency frame, preserved across a
   *                   trip to the ratio frame and back. Losing it there would make the ROI
   *                   row a destructive control: turn it on, turn it off, and the series
   *                   you had deliberately hidden is back.
   *
   * Plain `useState`, unlike `ScansByTime2`'s module store, for the reason its own note
   * gives: that chart's legend renders into the card SHELL's header while its plot renders
   * into the body, so the two are siblings with no common owner. Here the legend and the
   * plot are in ONE component and the ordinary answer is available.
   *
   * Resetting on collapse is correct rather than a limitation — this is a view preference
   * on a panel, not a saved setting, exactly as the mark toggle above is.
   *
   * ⚠️ THE DEFAULT IS NO LONGER A CONSTANT. It used to always open on `'currency'`; product
   * direction (2026-08-28, and the file-level note above `CHART_SERIES`) is that Return on
   * Vera leads once its trimmed series can draw anything, so the lazy initializer reads
   * `ratioAvailable` — computed above, from the ALREADY-GATED `activeSeries` — at the moment
   * this component first mounts (i.e. each time the disclosure is opened; `useState`'s
   * initializer runs once per mount, which is exactly the "resets on collapse" behaviour
   * this state already had). A company that clears the gate opens straight into the ROI
   * frame; a company that has not opens into currency AND meets the visible banner in the
   * render below explaining why (`roiComingSoon`) — never a silent default with no
   * explanation for why the promised leading view isn't the one on screen.
   */
  const [frameChoice, setFrameChoice] = useState<ValueChartAxis>(() =>
    ratioAvailable ? 'ratio' : 'currency',
  )
  const [hiddenCurrency, setHiddenCurrency] = useState<readonly ValueChartSeriesId[]>([])

  /**
   * The frame actually in force.
   *
   * DERIVED, never trusted from state: `frameChoice` can say `'ratio'` while the payload
   * carries no `running_multiple` at all (a range with no invoice in it — the ordinary case
   * on a brand's first month, and the case this whole card degrades through). Rendering a
   * ratio frame then would mount a `<YAxis yAxisId="ratio">` whose only mark is not in
   * `activeSeries`, which is precisely the recharts invariant that throws through the global
   * ErrorBoundary and presents as a HOME crash. Falling back to currency is the honest
   * degradation and it is unreachable-by-construction rather than guarded after the fact.
   */
  const frame: ValueChartAxis = frameChoice === 'ratio' && ratioAvailable ? 'ratio' : 'currency'

  /**
   * Is this series drawn right now?
   *
   * The exclusivity lives HERE, in one expression, rather than being spread across the
   * toggle handler and the render. A series outside the current frame is never visible, no
   * matter what the hidden set says.
   */
  const isVisible = (series: ValueChartSeries): boolean =>
    series.axis === frame && !hiddenCurrency.includes(series.id)

  // `useMemo` over `isVisible` rather than over its inputs: the predicate closes on `frame`
  // and `hiddenCurrency`, both of which are listed, so the memo is correct while the rule
  // that would verify it (`react-hooks/exhaustive-deps`) is not configured in this project.
  const visibleSeries = useMemo(
    () => activeSeries.filter(s => s.axis === frame && !hiddenCurrency.includes(s.id)),
    [activeSeries, frame, hiddenCurrency],
  )

  /**
   * ── THE LEGEND IS NOT `activeSeries`, AND THAT IS THE WHOLE FIX ────────────────────
   *
   * 🚨 A SERIES WITH NO DATA USED TO LEAVE NO TRACE. `activeSeries` tests the payload, so a
   * range in which cumulative spend never rises above zero — every `running_multiple` null,
   * which is the ordinary state of a brand that has not been invoiced yet — dropped `roi`
   * from the config, from the axes, from the marks AND from the legend. The card is titled
   * "Return on Vera"; a reader who came looking for that line found no line, no toggle, and
   * nothing saying why. "Off by default" and "absent entirely" look identical from outside,
   * and only one of them is a control you can go and find.
   *
   * So the legend carries ONE ROW MORE than the chart can draw: the return row is always
   * listed, and when there is nothing to plot it is listed DISABLED with the cause
   * (`roiUnavailableReason`). Absence becomes a stated fact instead of a silence.
   *
   * ⚠️ IT DOES NOT FEED THE PLOT. `activeSeries` still governs which `<YAxis>` mounts and
   * which marks are rendered, and nothing here touches either. That separation is load
   * bearing rather than tidy: recharts throws `Specifying a(n) yAxisId requires a
   * corresponding yAxisId on the targeted graphical component` if an axis mounts with no
   * mark carrying its id, and that throw escapes through the global ErrorBoundary and
   * presents as a HOME crash. A legend row is chrome; letting chrome decide what mounts is
   * how that invariant gets broken. The disabled row is also unclickable, so `frameChoice`
   * can never be driven to `'ratio'` while the ratio axis has nothing to draw.
   */

  /**
   * ── ONE REASON STRING, TWO SURFACES ─────────────────────────────────────────────────
   *
   * Read once and shared by the disabled legend row's tooltip AND the visible inline
   * banner (`roiComingSoon`, render below) — a diverging pair of strings describing the
   * same state is exactly the failure this constant exists to prevent (`ROI_GATE_REASON`'s
   * own note makes the same argument).
   *
   * PRIORITY, most specific first:
   *   1. `ratioPlottablePoints` is 1..(min − 1) — a return IS defined, but for too few
   *      buckets to draw a curve from (`ROI_SINGLE_POINT_REASON`). This can only be
   *      reached once the billing gate below is ALREADY met, because the head trim runs
   *      after it, so it has to be tested first: (2) would otherwise tell a reader who is
   *      looking at a multiple in the hero that there aren't two billing periods yet.
   *   2. `spendBucketsSettled` is 1..(gate − 1) — spend genuinely exists (at least one
   *      settled invoice) but hasn't cleared the stability gate yet. This is the "coming
   *      soon" state `ROI_GATE_REASON` was written for.
   *   3. `spendSource` is set and the count is 0 (or any other cause) — the pre-existing,
   *      per-cause messages in `ROI_UNAVAILABLE_REASON`. Note `stripe_invoices` with ZERO
   *      settled buckets deliberately falls through to here rather than to (2): "at least
   *      two billing periods" is less precise than "nothing settled in this period" when
   *      there is nothing at all yet, and the existing sentence already covers a brand
   *      mid-history who has simply filtered to a quiet window.
   *   4. No `spendSource` yet (caller hasn't passed it) — the cause-free fallback, saying
   *      only what is certainly true.
   */
  const roiUnavailableReason = useMemo(() => {
    if (ratioPlottablePoints > 0 && ratioPlottablePoints < ROI_MIN_PLOTTABLE_POINTS) {
      return ROI_SINGLE_POINT_REASON
    }
    if (spendBucketsSettled > 0 && spendBucketsSettled < ROI_GATE_MIN_SPEND_BUCKETS) {
      return ROI_GATE_REASON
    }
    if (spendSource) return ROI_UNAVAILABLE_REASON[spendSource]
    return 'There is no Vera spend to divide by over this period.'
  }, [ratioPlottablePoints, spendBucketsSettled, spendSource])

  /**
   * The VISIBLE half of the "one reason, two surfaces" pair above — see `ROI_GATE_REASON`
   * for why hover-only isn't enough here. Scoped to `spendSource === 'stripe_invoices'`
   * deliberately: this banner is specifically the "you're a real, billed customer, Return
   * is coming, here's what's missing" message, and it would be the wrong thing to show a
   * trial/no-billing-account company (`ROI_UNAVAILABLE_REASON.no_billing_account` is a
   * different, non-"soon" state) or a company hitting a transient fetch error
   * (`ROI_UNAVAILABLE_REASON.unavailable`) — both keep their existing tooltip-only
   * treatment, unchanged by this section.
   */
  const roiComingSoon = spendSource === 'stripe_invoices' && !ratioAvailable

  const legendRows = useMemo(() => {
    const rows: Array<{series: ValueChartSeries; disabledReason: string | null}> =
      activeSeries.map(series => ({series, disabledReason: null}))

    if (!ratioAvailable) {
      const roi = CHART_SERIES.find(s => s.axis === 'ratio')
      if (roi) {
        // `unshift`, not `push`: Return leads the legend regardless of whether it can
        // currently be drawn — see the `CHART_SERIES` ordering note. When it IS active this
        // is already a no-op (roi is already first in `activeSeries`, since it's first in
        // `CHART_SERIES`); this branch only fires while it's disabled, where the array would
        // otherwise put it last.
        rows.unshift({series: roi, disabledReason: roiUnavailableReason})
      }
    }

    return rows
  }, [activeSeries, ratioAvailable, roiUnavailableReason])

  /**
   * ── THE TOGGLE, AND WHY CLICKING A CURRENCY ROW IN THE RATIO FRAME SWITCHES BACK ───
   *
   * Clicking a legend row means "show me this". In the ratio frame that is only expressible
   * by leaving the ratio frame, so it does — and it also shows the row that was clicked,
   * because the alternative (switch frames but leave the clicked series hidden) answers a
   * request to show something by showing nothing.
   *
   * 🚨 THAT ARRIVAL SHOWS THE CLICKED SERIES **ALONE**, and the difference is not cosmetic.
   * This branch used to `filter` the clicked id out of `hiddenCurrency` — which reads as
   * "un-hide what was clicked" and is right in isolation, but `hiddenCurrency` is `[]` on
   * every ordinary arrival (the reader has been in the ratio frame since mount and has
   * therefore never hidden anything). Filtering `[]` yields `[]`, so NOTHING was hidden and
   * BOTH currency series appeared from a single click on one of them: clicking "Value
   * delivered" silently switched on "Spend with Vera" as well. So the transition states the
   * selection positively — hide every OTHER currency series — rather than subtracting from a
   * set that is usually empty. From here the two toggle independently on the ordinary path
   * below, which is the whole ask.
   *
   * Derived from `CHART_SERIES`, not `activeSeries`: `activeSeries` is payload-dependent, so
   * a currency series absent from THIS range would be left out of the hidden set and would
   * pop into view unbidden if a later fetch carried it. Hiding a series that is not active is
   * a no-op — it is not drawn either way — so the config is the safe set to enumerate.
   *
   * ⚠️ THIS IS NOT THE EXCLUSIVITY BEING LOOSENED. Frame exclusivity (ratio vs currency,
   * `CHART_SERIES`'s block) is untouched and must stay: ROI is a RATIO and these two are
   * CURRENCY, so they cannot share a y-axis. What is now independent is VALUE vs SPEND —
   * both currency, both on the one axis — which was always allowed and merely could not be
   * reached from this transition.
   *
   * The ROI row is a straight frame swap. Note it does NOT touch `hiddenCurrency`: see the
   * state block above for why that selection has to survive the round trip.
   *
   * Hiding EVERY currency series is allowed and yields an empty plot with its axes intact,
   * matching `ScansByTime2` exactly. It is not a crash surface here because every mark stays
   * MOUNTED with `hide` — see the render.
   */
  const toggleSeries = (series: ValueChartSeries) => {
    if (series.axis === 'ratio') {
      setFrameChoice(current => (current === 'ratio' ? 'currency' : 'ratio'))
      return
    }
    if (frame === 'ratio') {
      setFrameChoice('currency')
      setHiddenCurrency(
        CHART_SERIES.filter(s => s.axis === 'currency' && s.id !== series.id).map(s => s.id),
      )
      return
    }
    setHiddenCurrency(hidden =>
      hidden.includes(series.id) ? hidden.filter(id => id !== series.id) : [...hidden, series.id],
    )
  }

  /**
   * ── THE GRADIENT FILL, AND THE RULE THAT KEEPS IT HONEST ───────────────────────────
   *
   * Lifted verbatim from `ScansByTime2`, including the constraint, because the constraint is
   * the whole reason the fill is allowed at all: **fill only when exactly ONE series is
   * visible.** Overlapping translucent areas rank by DRAW ORDER rather than by value, and
   * where they overlap they composite into a hue that matches no swatch in the legend — so
   * two filled areas are a picture of the draw order, not of the data.
   *
   * Toggling is what makes the rule generous rather than restrictive: "one visible series"
   * is now something a reader can REACH by hiding the others, which is exactly the moment
   * the fill is both honest and useful. The ratio frame is always single-series, so it is
   * always filled.
   *
   * BARS NEVER GET A FILL. A bar is already a filled shape; a gradient area under one is a
   * second mark stating the same thing. Hence the `mark === 'line'` gate.
   */
  const filledSeriesId =
    mark === 'line' && visibleSeries.length === 1 ? visibleSeries[0].id : null

  /**
   * Gradient ids are per SERIES, not one shared id.
   *
   * An SVG paint server is referenced by id, so a single hardcoded id whose stops vary by
   * series silently keeps whichever colour owns that id at paint time — across two charts
   * on one page, or across a stale def surviving a re-render. Keying the id to the series
   * makes the reference unambiguous, and the `defs` below emits only the one in use.
   */
  const gradientIdFor = (id: string) => `roiValueFill-${id}`

  return (
    <Box>
      {/*
        ⚠️ THE SUMMARY LINE WAS REMOVED, and it is not coming back in that form.

        It read `$240,640 across 12 months · Sep 2 – Aug 30` and was defended as restating
        the panel's scope for a reader who had just opened a disclosure. What it actually
        did was make that scope look CONTESTED: the hero two lines above states "All time ·
        since Sep 2, 2025", so a reader met "12 months" underneath it and had to work out
        whether the chart was showing something narrower. It was not — `points.length` is
        the BUCKET COUNT of the very same window — but a line whose job is reassurance is
        failing when it prompts the check. Reported by the user as exactly that question.

        Every part of it is already on screen: the total is the 56px figure above, the range
        is the hero's own scope line, and the bucket count is the x-axis. So it is dropped
        rather than reworded — restating three things the surface already says is what made
        it noise in the first place.

        The mark toggle keeps the row and stays pinned right, matching `ScansByTime2`'s
        header (same `ViewToggle`, same glyphs, same side) so the two charts read as one
        system (§28/§36). The legend now leads the row.
      */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={3}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={4} flexWrap="wrap" useFlexGap>

          {/*
            THE LEGEND EXISTS ONLY WHEN THERE IS SOMETHING TO TELL APART.

            With one series it would name what the card's title, the summary line to its
            left and the 56px figure above it all already name — `ScansByTime2` suppresses
            its own legend on exactly this reasoning, and billing has none for the same. So
            `> 1`: with no invoice in range the payload carries value alone and this
            disappears entirely.

            ⚠️ IT IS NO LONGER PASSIVE. A previous note here argued against click-to-hide,
            on the grounds that "three fixed series of which one is the card's whole subject
            is not a set anyone needs to isolate". Two things overturned that. Explicit
            product direction (2026-08-28) asks for the same toggling `ScansByTime2` has —
            and, more substantively, isolation is now what makes the ROI series expressible
            at all: it lives on a RATIO axis and can only be drawn by taking the currency
            frame off screen (see the exclusivity block in `CHART_SERIES`). A passive legend
            has nowhere to put it.

            Swatch colour reads from the same `series.color(theme)` the mark is stroked
            with — one source, so a swatch cannot drift from the line it names. The HOLLOW
            swatch is `ScansByTime2`'s second channel, carried over: a hidden row differs in
            fill AND in label colour, never in colour alone.

            `aria-pressed` carries the state and the label names the THING, because a
            control that announces both ends up read out contradicting itself.
          */}
          {legendRows.length > 1 && (
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap alignItems="center">
              {/*
                `Display:` — the same label pattern as the map's `Group by:`
                (`AnalyticsMapGroupingToggle`), deliberately: `variant="body3"`,
                `text.secondary`, no-wrap, in a row with the controls it names.

                It is honest here because these legend rows ARE controls — each is a real
                `<button>` with `onClick={toggleSeries}` and `aria-pressed`. A passive key
                would not have earned a colon: prefixing one would advertise a control
                that is not there, which is the same defect as an affordance that
                silently no-ops.

                Also gives the row a text anchor. Without it the first thing in the band
                is a bare colour swatch, which reads as decoration until you have already
                worked out that the swatches are clickable.
              */}
              <Typography
                variant="body3"
                sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                Display:
              </Typography>
              {legendRows.map(({ series, disabledReason }) => {
                const color = series.color(theme)
                const disabled = disabledReason !== null
                const shown = !disabled && isVisible(series)

                const row = (
                  <Stack
                    component="button"
                    type="button"
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    // NO-OP RATHER THAN `disabled`, and the reason is the tooltip. A
                    // genuinely `disabled` button emits no pointer events, so MUI's Tooltip
                    // never opens on it and the sentence explaining the row becomes
                    // unreachable — which defeats the entire point of showing the row
                    // (§46: a disabled control must carry a readable reason). `aria-disabled`
                    // announces the state, the handler declines the click, and hover AND
                    // keyboard focus both still surface the explanation.
                    onClick={disabled ? undefined : () => toggleSeries(series)}
                    aria-disabled={disabled || undefined}
                    aria-pressed={disabled ? undefined : shown}
                    aria-label={`${series.label} series`}
                    // NO `title` ON THE DISABLED ROW — it is wrapped in a Tooltip below, and
                    // MUI warns loudly, per instance, when a Tooltip child also carries one.
                    title={disabled ? undefined : shown ? `Hide ${series.label}` : `Show ${series.label}`}
                    sx={{
                      appearance: 'none',
                      background: 'none',
                      border: 'none',
                      p: 0,
                      font: 'inherit',
                      // `help`, not `not-allowed`: there IS something to read here, and
                      // `not-allowed` tells a reader to stop rather than to hover.
                      cursor: disabled ? 'help' : 'pointer',
                      '&:focus-visible': {
                        outline: `2px solid ${theme.palette.brand[500]}`,
                        outlineOffset: 2,
                        borderRadius: 1,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        // HOLLOW when hidden. An `inset` ring rather than a border so the
                        // swatch keeps its 10px footprint in both states — a border would
                        // grow it and shuffle the row every time it is clicked.
                        //
                        // A DISABLED row is hollow too, and its ring is DIMMED rather than
                        // the series colour: hidden and undrawable are different facts and
                        // must not render identically. Hidden says "you turned this off";
                        // disabled says "there is nothing to turn on".
                        backgroundColor: shown ? color : 'transparent',
                        boxShadow: shown
                          ? 'none'
                          : `inset 0 0 0 2px ${disabled ? theme.palette.text.disabled : color}`,
                        flexShrink: 0,
                      }}
                    />
                    {/* The LABEL carries identity; colour is never the only channel. */}
                    {/*
                      ⚠️ CORRECTED 2026-09-03: the note this replaced asserted the pill and
                      this label were "ALREADY the same 12px" and that only the WEIGHT
                      differed. That was measured against the wrong element — the certificate-
                      status FILTER PILL (the applied-filter chip, `TagChip` in
                      `AnalyticsAppliedFilters`) is 13px/500 live in the browser
                      (`getComputedStyle`, 1280px viewport), not 12px. Product direction
                      (2026-09-03) also asked for both the font AND the swatch to come up —
                      this legend read as a footnote next to the pill, not a peer.

                      So both axes moved: `fontSize: 13` (was the `caption` variant's default
                      12px) to match the FILTER PILL's measured size, and the swatch above
                      went 8px → 10px, sized against the Scans chart's OWN status pill (the
                      other half of this change) rather than picked blind — see that file's
                      `SCANS_STATUS_PILL_SIZE_SX` docblock for its pill's now-28px-tall,
                      16px-icon proportions. 10px reads as a peer of that 16px icon at this
                      swatch's much lighter visual weight (a flat fill vs. a bolder icon
                      glyph) without ballooning into looking like a clickable button icon.
                      `fontWeight: 500` still matches the pill's weight, unchanged from before.
                    */}
                    <Typography
                      variant="caption"
                      color={shown ? 'text.secondary' : 'text.disabled'}
                      sx={{ whiteSpace: 'nowrap', fontWeight: 500, fontSize: 13 }}
                    >
                      {series.label}
                    </Typography>
                  </Stack>
                )

                // The disabled row's whole purpose is the sentence, so it gets a real
                // Tooltip; the enabled rows keep the native `title` they already had.
                return disabled ? (
                  <Tooltip
                    key={series.id}
                    arrow
                    placement="top"
                    title={disabledReason}
                    slotProps={{ tooltip: { sx: { maxWidth: 300, lineHeight: 1.5 } } }}
                  >
                    {row}
                  </Tooltip>
                ) : (
                  <React.Fragment key={series.id}>{row}</React.Fragment>
                )
              })}
            </Stack>
          )}
        </Stack>

        <ViewToggle
          options={MARK_OPTIONS}
          value={mark}
          onChange={setMark}
          ariaLabel="Chart type"
        />
      </Stack>

      {/*
        An all-zero series is still PLOTTED, and the sentence is added rather than
        substituted. "No value delivered" is a fact about the period, not a missing
        response, and replacing the axes with an empty state would make the two
        indistinguishable — the reader could not tell whether Vera measured nothing or
        measured zero. The flat baseline against a labelled time axis says the second.

        ONE sentence, about THIS PERIOD. The second sentence this used to carry described
        what the chart MEASURES, which is not empty-state copy at all — it is equally true
        of a full chart, and pinning it here made it readable only when there was nothing to
        apply it to. It now lives on the disclosure title's info tooltip; see
        `ROI_VALUE_CHART_SCOPE`.
      */}
      {total === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5, mb: 2 }}>
          No recovered or resale value in this period.
        </Typography>
      )}

      {/*
        ── THE VISIBLE HALF OF THE "RETURN IS COMING" MESSAGE ──────────────────────────

        `roiComingSoon` / `ROI_GATE_REASON` — see `roiUnavailableReason`'s own note for the
        full priority rule and why this is the SAME string as the disabled legend row's
        tooltip rather than a second one.

        🚨 THIS IS THE "REAL PLACEHOLDER, NOT A SILENT FALLBACK" REQUIREMENT, and it is
        rendered in the CURRENCY frame's body rather than inside a mounted-but-empty ratio
        frame on purpose: the axis-mount invariant this file guards everywhere else means an
        empty ratio `<YAxis>` with no mark is a crash surface, not a UI state, so there is
        never a literal blank "Return" panel to fill in. `frame` already resolves to
        `'currency'` whenever `!ratioAvailable` (see that derivation), so a brand who hasn't
        cleared the gate is ALWAYS looking at value + spend — this banner is what explains,
        without requiring a hover, that this is a fallback rather than the plan. A brand
        past the gate never sees it: `roiComingSoon` is false the moment `ratioAvailable`
        is true, which is also the moment the ratio frame becomes the default (see the
        `frameChoice` initializer above).
      */}
      {roiComingSoon && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5, mb: 2 }}>
          {roiUnavailableReason}
        </Typography>
      )}

      <Stack sx={{ height: CHART_HEIGHT, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          {/*
            `ComposedChart`, not `BarChart`, and not swapped for `LineChart` per mark.
            Two reasons, and the second is the one that matters beyond today:

             1. A `<Bar>` and a `<Line>` can be children of the SAME chart element, so
                toggling the mark changes one child rather than remounting the chart, its
                axes and its tooltip. `ScansByTime2` makes the same choice for the same
                reason.
             2. It is the shape a SECOND series needs. The TODO above names invoice-backed
                spend as the honest source for a spend series, and a real ROI-over-time
                line on top of it — value as bars with spend as a line is the canonical
                rendering of that pair, and it is only expressible in a composed chart. So
                adding it later is a new child here, not a chart-type migration.
          */}
          <ComposedChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            {/* Only the ONE gradient actually in use is emitted, and its id names the
                series it belongs to — see `gradientIdFor` for why a single shared id is a
                trap once the colour varies by series. */}
            <defs>
              {activeSeries
                .filter(s => s.id === filledSeriesId)
                .map(s => (
                  <linearGradient key={s.id} id={gradientIdFor(s.id)} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={s.color(theme)} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={s.color(theme)} stopOpacity={0} />
                  </linearGradient>
                ))}
            </defs>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              tickLine={false}
              axisLine={false}
              // Gap-based thinning rather than `interval={0}`: a billing period at daily
              // granularity is ~30 buckets and every label will not fit, while a monthly
              // range is ~12 and they all will. One rule covers both.
              minTickGap={24}
            />
            {/*
              ── TWO AXES ARE DECLARED. ONLY ONE IS EVER VISIBLE. ────────────────────────

              🚨 THIS IS NOT A DUAL-AXIS CHART, and the distinction is the whole design.
              A dual axis renders two scales AT ONCE and asks the reader to work out which
              one each mark belongs to — which is what was built here, rendered, and
              rejected (see the exclusivity block in `CHART_SERIES`). Here the two axes are
              MUTUALLY EXCLUSIVE FRAMES: `hide` takes the out-of-frame one off screen
              entirely, so at any instant there is exactly one scale and every drawn mark
              belongs to it.

              ⚠️ WHY `hide` RATHER THAN NOT RENDERING IT. recharts throws
              `Specifying a(n) yAxisId requires a corresponding yAxisId on the targeted
              graphical component` whenever an axis mounts with no mark carrying its id OR
              a mark carries an id no axis declares — a throw that escapes through the
              global ErrorBoundary and presents as a HOME crash, because Home is the
              redirect TARGET. Conditionally mounting axis and marks means two lists that
              must agree on every render, and they only have to disagree once. Mounting
              both axes and every active mark, and expressing visibility ONLY through
              `hide`, makes the invariant hold by construction.

              The one thing that IS conditional is `activeSeries` membership: an axis is
              declared only when the payload actually carries a series for it, which is the
              same expression the marks map over. One source, so they cannot drift.
            */}
            {activeSeries.some(s => s.axis === 'currency') && (
              <YAxis
                yAxisId="currency"
                hide={frame !== 'currency'}
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                tickLine={false}
                axisLine={false}
                width={56}
                // Currency is a fact about the figure, not a display preference — same
                // reporting currency as the series it labels.
                tickFormatter={(cents: number) => formatMoneyAxis(cents, currency)}
                /**
                 * ONE tick when every bucket is zero.
                 *
                 * Left to itself recharts computes the domain [0, 0] and still emits its
                 * default five ticks — five rows all reading `$0`, stacked up the axis,
                 * which is what an all-zero period rendered before this line. It reads as a
                 * broken axis rather than as an empty period, and an axis that looks broken
                 * undermines the numbers beside it.
                 */
                {...(maxValue === 0 ? { ticks: [0] } : {})}
              />
            )}
            {activeSeries.some(s => s.axis === 'ratio') && (
              <YAxis
                yAxisId="ratio"
                hide={frame !== 'ratio'}
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                tickLine={false}
                axisLine={false}
                width={56}
                /* `2.5×`, not `2.5`. The unit is the entire difference between this axis
                   and the currency one, and a bare number beside a chart whose other frame
                   is dollars is the ambiguity the exclusivity exists to remove. */
                tickFormatter={(value: number) => `${value}×`}
              />
            )}
            {/*
              A CUSTOM tooltip body, not `formatter`, and the reason is structural rather
              than stylistic. The breakdown lives on fields the chart does not plot
              (`stolen_recovery_cents` / `resale_value_cents`), and recharts' `formatter`
              only ever sees the series it drew. The obvious workaround — declaring the two
              contributors as extra `<Bar>`s with `barSize={0}` so they reach the payload —
              is wrong: recharts divides each category's width between every `Bar` in the
              chart, so two invisible siblings would shrink the visible bar to a third of
              its slot and shove it off centre. Reading the datum straight off the payload
              costs one small component and moves nothing.
            */}
            {/* `ScansByTime2`'s pairing, for the same reason: a hovered BAR wants its
                column washed, a hovered point on a line wants a vertical rule to read
                against the axis. */}
            <RechartsTooltip
              cursor={
                mark === 'bar'
                  ? { fill: theme.palette.action.hover }
                  : { stroke: theme.palette.grey[300] }
              }
              content={<ValueTooltip series={visibleSeries} currency={currency} />}
            />
            {/*
              ONE MARK PER ACTIVE SERIES, from the config. Today that is exactly one; when
              spend and ROI arrive it is three, with no edit here.

              The mark form comes from the series (`mark: 'toggle' | 'line'`), so the
              Line/Bar control governs the VALUE series only. That is not a limitation being
              worked around: value arrives as discrete events and has an honest bar reading,
              while a recurring charge and a derived ratio do not, so offering "bar" for
              those would be offering a wrong picture.
            */}
            {activeSeries.map(series => {
              const color = series.color(theme)
              const useBar = series.mark === 'toggle' && mark === 'bar'
              // VISIBILITY IS A PROP, NEVER A MOUNT — see the axis block above for the
              // recharts invariant that makes conditional mounting a crash surface.
              const hide = !isVisible(series)

              return useBar ? (
                <Bar
                  key={series.id}
                  hide={hide}
                  yAxisId={series.axis}
                  dataKey={series.id}
                  name={series.label}
                  fill={color}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={40}
                  /* OFF, matching every other bar series in the app. recharts renders a
                     `Bar`'s rectangles from INSIDE its `Animate` render-prop, so with
                     animation on there is no `<path>` at all until `requestAnimationFrame`
                     first ticks — which in the Claude preview pane is never, and in a
                     backgrounded or printed tab is late. */
                  isAnimationActive={false}
                />
              ) : (
                /*
                  `Area`, not `Line`, and it is `Line` with a fill whenever the fill rule
                  says no — `fill="transparent"` makes an `Area` behave exactly as the
                  `Line` it replaces. Using ONE component for both cases is what stops
                  recharts seeing a different element TYPE and remounting the series every
                  time the visible count crosses 1; `ScansByTime2` makes the same choice for
                  the same reason.
                */
                <Area
                  key={series.id}
                  hide={hide}
                  yAxisId={series.axis}
                  type="monotone"
                  dataKey={series.id}
                  name={series.label}
                  stroke={color}
                  strokeWidth={2}
                  /* The fill rule (`filledSeriesId`): a gradient ONLY when this is the one
                     visible series. Same 0.3 → 0 stops as `ScansByTime2` and
                     `BillingOverview`, so the three charts are the same object when they
                     are doing the same job. */
                  fill={series.id === filledSeriesId ? `url(#${gradientIdFor(series.id)})` : 'transparent'}
                  fillOpacity={1}
                  /* NO RESTING DOT, matching `ScansByTime2` exactly. See the mark block at
                     the top of this file for the sparse-series argument that used to put one
                     here and why it was overruled. `activeDot` STAYS — that chart keeps its
                     hover dot too, and it is the thing that lets a reader point at a bucket
                     and read it, rather than a mark competing for attention at rest. */
                  dot={false}
                  activeDot={{
                    r: 4,
                    stroke: color,
                    strokeWidth: 2,
                    // Surface-coloured, not a hardcoded white — on the dark theme a `#fff`
                    // ring is a bright hole punched in the line.
                    fill: theme.palette.background.paper,
                  }}
                  /* `connectNulls` OFF, and it matters most on the ROI series: every bucket
                     before the range's first invoice has a null multiple, and joining
                     across them would draw a straight line through months where the return
                     was not defined rather than zero. */
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </Stack>
    </Box>
  )
}
